import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Lock, Search } from "lucide-react";

const CATEGORIES = [
  { slug: "", name: "All" },
  { slug: "weight_loss", name: "Weight Loss" },
  { slug: "detox", name: "Detox & Heavy Metals" },
  { slug: "cognitive", name: "Cognitive Health" },
  { slug: "general_wellness", name: "General Wellness" },
];

export default function Library() {
  const { user } = useAuth();
  const isPaid = user && (user.is_paid || user.role === "admin");
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const [plants, setPlants] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const url = isPaid ? `/plants${cat ? `?category=${cat}` : ""}` : "/plants/preview";
    api.get(url).then((r) => setPlants(r.data)).catch(() => setPlants([]));
  }, [cat, isPaid]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = plants;
    if (!isPaid && cat) list = list.filter((p) => p.categories?.includes(cat));
    if (!needle) return list;
    return list.filter(
      (p) =>
        p.common_name.toLowerCase().includes(needle) ||
        p.scientific_name.toLowerCase().includes(needle) ||
        (p.summary || "").toLowerCase().includes(needle)
    );
  }, [plants, q, isPaid, cat]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow">The field guide</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-2">The Library</h1>
          <p className="text-[var(--ink-soft)] mt-3 max-w-xl">
            {isPaid
              ? "Every plant, fully unlocked. Click any entry for identification, preparation, dosage and cautions."
              : "Preview the library below. Unlock the full profiles — identification, dosage, recipes and cautions — for $4."}
          </p>
        </div>

        {!isPaid && (
          <Link data-testid="library-unlock-cta" to="/unlock" className="btn-accent">
            <Lock className="w-4 h-4" /> Unlock all for $4
          </Link>
        )}
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            data-testid={`library-cat-${c.slug || "all"}`}
            key={c.slug}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (c.slug) next.set("cat", c.slug);
              else next.delete("cat");
              setParams(next);
            }}
            className={`chip transition-colors ${cat === c.slug ? "bg-[var(--forest)] text-[var(--paper)] border-[var(--forest)]" : ""}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3 border-b border-[var(--border-line)] py-2">
        <Search className="w-4 h-4 text-[var(--ink-soft)]" />
        <input
          data-testid="library-search-input"
          className="bg-transparent outline-none flex-1 text-sm"
          placeholder="Search by common or scientific name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => {
          const card = (
            <article className="card-journal h-full flex flex-col" data-testid={`plant-card-${p.slug}`}>
              <div className="aspect-[4/3] overflow-hidden frame-border p-0 mb-4">
                <img src={p.image_url} alt={p.common_name} className="w-full h-full object-cover" />
              </div>
              <p className="font-mono text-xs text-[var(--sage)] italic">{p.scientific_name}</p>
              <h3 className="font-serif text-2xl mt-1">{p.common_name}</h3>
              <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed line-clamp-3 flex-1">{p.summary}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(p.categories || []).slice(0, 2).map((c) => (
                  <span key={c} className="chip">{c.replace("_", " ")}</span>
                ))}
              </div>
              {!isPaid && (
                <div className="mt-4 text-xs text-[var(--terracotta)] font-mono flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Unlock for full profile
                </div>
              )}
            </article>
          );
          return isPaid ? (
            <Link to={`/plants/${p.slug}`} key={p.slug}>{card}</Link>
          ) : (
            <Link to="/unlock" key={p.slug}>{card}</Link>
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full text-[var(--ink-soft)] italic">No plants match that search yet.</p>
        )}
      </div>
    </div>
  );
}
